import { create } from 'zustand';

/**
 * 단기매매 화면이 지금 어느 시장을 보고 있는가. (2026-09-19, I-53)
 *
 * 🔴 왜 라우터가 아니라 스토어인가
 *   `/trading/us/...` 처럼 경로를 나누면 App.tsx 를 건드려야 하는데 그건 공유 영역이다
 *   (CLAUDE.md 4.2). 그리고 경로를 나누면 같은 화면이 두 벌이 되어, 한쪽만 고치는 실수가 생긴다.
 *   탭 안에서 시장만 바꾸면 화면은 한 벌이고 데이터 출처만 갈린다.
 *
 * 🔴 왜 URL 쿼리가 아닌가
 *   쿼리로 두면 페이지를 옮길 때마다 `?market=US` 를 이어 붙여야 하고, 한 군데라도 빠지면
 *   사용자가 미국을 보다가 종목을 눌렀을 때 조용히 국내로 돌아온다.
 *
 * 무엇이 이 값을 따르나 (I-53 확정)
 *   시장별  추천 · AI 모의투자 · 주도주 · 섹터 · 지난 추천
 *           · 가격 알림(2026-09-28 사용자 결정, I-83 ① — 전엔 공통이라 두 나라가 한 목록에 섞였다)
 *           · 시장지표의 국고채 · 지수 하위 탭(2026-10-17 사용자 Q17 — marketSeries.ts US_TABS)
 *   공통    관심종목 · 카테고리 — 한 목록에 시장 배지를 달고 필터로 거른다 · 시장지표의 환율 · 비트코인 · 금
 */
export type MarketScope = 'KR' | 'US';

const STORAGE_KEY = 'trading.marketScope';

/**
 * 저장된 값을 읽는다. 없거나 깨졌으면 국내다.
 *
 * localStorage 접근 자체가 던질 수 있다 - 사파리 프라이빗 모드나 저장소 차단 설정이다.
 * 화면이 통째로 죽는 것보다 국내로 시작하는 편이 낫다.
 */
function readStored(): MarketScope {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'US' ? 'US' : 'KR';
  } catch {
    return 'KR';
  }
}

interface MarketScopeState {
  scope: MarketScope;
  setScope: (scope: MarketScope) => void;
}

export const useMarketScope = create<MarketScopeState>((set) => ({
  scope: readStored(),
  setScope: (scope) => {
    try {
      localStorage.setItem(STORAGE_KEY, scope);
    } catch {
      // 저장 못 해도 이번 세션 동안은 동작한다. 다음에 국내로 돌아갈 뿐이다.
    }
    set({ scope });
  },
}));

/**
 * 이 시장의 데이터가 준비돼 있나. (2026-09-19 · 2026-09-20 사실 갱신)
 *
 * 미국은 종목 마스터 10,662종목과 **일봉 132종목 3년치**(2026-09-19 백필)가 있고,
 * 2026-09-20 부터 매일 10:30 증분 수집이 돈다(I-71 P1) - 데이터는 더 이상 멈춰 있지 않다.
 * 그런데도 아직 준비 중인 이유는 데이터가 아니라 **계좌와 추천이 없어서**다 -
 *   td_paper_account 는 profile 3행 · td_band_account 는 variant 3행이고 국가 컬럼 자체가 없다
 *   추천 생산이 코스피로 잠겨 있다 (ScreeningService 의 TARGET_MARKETS)
 * 그래서 추천·모의투자·지난 추천이 통째로 비어 있다. 화면이 "결과 없음" 을 보여주면
 * 사용자는 오늘 후보가 없는 것으로 읽는다 - 아직 만들지 않은 것과는 전혀 다른 상태다.
 *
 * 미국 계좌가 열리는 시점(BACKLOG I-64 · U-16)에 이 함수를 지우면 된다.
 *
 * ✏️ 2026-09-28 I-78 2차 — 위 사정이 반쯤 바뀌었다. 미국 저점매수 모의 계좌 US_P20 이 생겼고(V141 —
 *   td_band_account 에 행 · 변형에 나라가 붙었다), AI 모의투자 화면은 미국이면 UsPaperView 로 우회해
 *   이 함수를 거치지 않는다(PaperPage). 🔴 그래도 지우지 않는다 — 미국 **추천**(ScreeningService TARGET_MARKETS)은
 *   여전히 없어서 추천 · 지난 추천 화면을 막는 데 계속 필요하다. 미국 추천이 붙는 날 지운다.
 */
export function isMarketReady(scope: MarketScope): boolean {
  return scope === 'KR';
}

/**
 * 지금 고른 시장이 준비됐나. 화면이 **early return** 하는 데 쓴다. (2026-09-20)
 *
 * 🔴 이것이 있어야 하는 이유
 *   다섯 화면이 MarketNotReady 를 배너로 위에만 얹고 국내 데이터를 그대로 아래에 그렸다.
 *   미국을 고른 채 AI 모의투자를 열면 "준비 중" 과 국내 계좌 6개가 같이 보였다.
 *   화면마다 useMarketScope 를 풀어 쓰는 대신 이 한 줄로 막는다.
 */
export function useMarketReady(): boolean {
  return isMarketReady(useMarketScope((s) => s.scope));
}

/**
 * 화면에 뜨는 이름. 코드 식별자와 가른다 (BandVariant.label 과 같은 원칙)
 *
 * 🔴 "해외주식" 이 아니라 "미국주식" 이다 (2026-09-20 사용자 지시)
 *   지원하는 해외 시장이 미국(NASDAQ · NYSE · AMEX)뿐이라 "해외" 는 실제보다 넓게 말한다.
 *   다른 나라가 붙는 날 이 함수와 MarketScope 타입을 함께 넓힌다.
 */
export function marketScopeLabel(scope: MarketScope): string {
  return scope === 'US' ? '미국주식' : '국내주식';
}

/**
 * 이 말 뒤에 붙일 주격 조사(은/는)를 고른다. (2026-09-20)
 *
 * 🔴 "미국주식는 준비 중입니다" 가 화면에 나왔다
 *   문구를 `${label}는` 으로 붙여 놨는데 받침이 있는 말에는 "은" 이다.
 *   시장 이름이 늘 때마다 같은 실수를 하지 않도록 받침을 보고 고른다.
 *
 * 한글 음절은 0xAC00 부터 (초성 x 중성 x 종성 28) 순서로 배열돼 있어,
 * (코드 - 0xAC00) % 28 이 0이면 종성(받침)이 없다.
 */
export function topicParticle(word: string): string {
  if (word.length === 0) {
    return '는';
  }
  const last = word.charCodeAt(word.length - 1);
  if (last < 0xac00 || last > 0xd7a3) {
    return '는'; // 한글 음절이 아니면(영문·숫자) 보수적으로 는
  }
  return (last - 0xac00) % 28 === 0 ? '는' : '은';
}

/**
 * 국내에만 있는 화면. 해외를 고르면 하위 탭에서 숨긴다. (2026-09-19 사용자 결정)
 *
 * 주도주 · 섹터는 업종 분류(td_sector_map)가 국내 종목에만 있다. 누를 수 없는 것을 보여주지 않는다.
 * 해외 업종 분류가 생기면 여기서 빼면 탭이 저절로 돌아온다.
 */
export const KR_ONLY_PATHS: readonly string[] = ['/trading/leading', '/trading/sectors'];

/**
 * 국내 · 미국이 함께 쓰는 화면. 스위치 옆에 "내용은 같다" 를 적는다. (2026-09-19 · 2026-09-20 개정)
 *
 * 🔴 숨기지 않는 이유 - 탭을 옮길 때마다 전환 줄이 생겼다 사라지면 화면이 위아래로 튄다.
 * 🔴 막지도 않는다 (2026-09-20 사용자 지시) - 전에는 disabled 로 잠갔는데, 그러면 환율을
 *   보다가 미국으로 넘어가려면 다른 탭을 먼저 눌러야 했다. 전환은 되고 탭 구성만 바뀐다.
 *
 * 🔴 관심종목은 여기 없지만 공통이다 - 추천 화면 안의 영역이라 경로가 따로 없다.
 *   한 목록에 시장 배지를 달고 필터로 거른다 (2026-09-20 사용자 확인 - "결국 카테고리다").
 *
 * 🔴 가격 알림은 여기서 뺐다 (2026-09-28 사용자 결정, I-83 ①)
 *   스위치로 국내 · 미국 알림을 가른다. "내용은 같다" 표시가 남으면 거짓말이 된다.
 *
 * ✏️ 2026-10-17 사용자 Q17 — 시장지표(/trading/fx)는 경로로는 공통이지만 안의 국고채 · 지수 하위 탭은 스위치를 따른다.
 *   하위 탭은 주소의 ?tab= 이라 경로만 보는 이 함수로는 못 가른다 — TradingNav 가 marketSeries.ts isCommonMarketTab 을 함께 본다
 */
export const COMMON_PATHS: readonly string[] = ['/trading/fx'];

/** 이 경로가 시장과 상관없는 공통 화면인가 */
export function isCommonPath(pathname: string): boolean {
  return COMMON_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

/** 이 경로의 탭을 이 시장에서 보여주나 */
export function isTabVisible(path: string, scope: MarketScope): boolean {
  return scope === 'KR' || !KR_ONLY_PATHS.includes(path);
}

/**
 * 시장을 바꾼 뒤 머물 경로. 국내 전용 화면에서 해외로 바꾸면 추천으로 옮긴다 - 그 탭이 사라지기 때문이다.
 *
 * @return 옮길 필요가 없으면 null
 */
export function pathAfterScopeChange(pathname: string, next: MarketScope): string | null {
  if (next === 'US' && KR_ONLY_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`))) {
    return '/trading';
  }
  return null;
}
